import React from 'react';

import CalendarIcon from 'icons/calendar.svg';
import DesktopMobileIcon from 'icons/desktop-mobile.svg';
import LaunchIcon from 'icons/launch.svg';
import { useTranslation } from 'react-i18next';

import OutlinedButton from 'components/application/buttons/OutlinedButton';

import { useCurrentActor } from 'components/contexts/CurrentActor';

import { formatDate } from 'components/helpers/dates';
import { resourceShape } from 'components/helpers/serialisableResources';
import { personDisplayName } from 'components/helpers/users';

export default function BookingDetails({
  booker,
  booking,
  course,
  registration,
}) {
  const currentActor = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'mobileTrainingProfile.components.courseDetailsScreen.BookingDetails',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const courseHasELearningCourse =
    currentActor.isAllowedFeature('e_learning') &&
    !!course.relationships.eLearningCourse?.data?.id;

  const { deletedAt, email: bookerEmail } = booker?.attributes || {};
  const { date, hashid, notes } = booking.attributes;

  const isBookerDeleted = !!deletedAt;

  return (
    <div className='tw-w-full tw-rounded-xl tw-bg-cyan-025 tw-p-4 tw-text-cyan-800'>
      {courseHasELearningCourse ?
        <>
          <div className='tw-inline-flex tw-text-m tw-font-semibold'>
            <DesktopMobileIcon className='tw-mr-1' height={20} width={20} />
            <span>{i18nT('description.eLearning')}</span>
          </div>
          {booker && (
            <div className='tw-mt-2'>
              <p className='tw-mb-0'>
                {i18nTCommon('bookings.bookedBy', {
                  interpolation: { escapeValue: false },
                  name: personDisplayName({
                    attributes: booker.attributes,
                  }),
                })}
                {isBookerDeleted && ` ${i18nTCommon('shared.deletedUser')}`}
              </p>
              {!isBookerDeleted && (
                <a
                  className='app-link tw-align-baseline tw-text-blue-500 hover:tw-text-blue-300 active:tw-text-blue-300'
                  href={`mailto:${bookerEmail}`}
                >
                  {bookerEmail}
                </a>
              )}
            </div>
          )}
          <div className='tw-mt-4'>
            <OutlinedButton
              color='grey'
              onClick={() => {
                window.open(`/e_learning/bookings/${hashid}`, '_blank');
              }}
              size='sm'
            >
              <span>
                {registration ?
                  i18nTCommon('courses.continueCourse')
                : i18nTCommon('courses.startCourse')}
              </span>
              <LaunchIcon className='-tw-mr-1 tw-ml-1' height={20} width={20} />
            </OutlinedButton>
          </div>
        </>
      : <>
          <div className='tw-inline-flex tw-text-m tw-font-semibold'>
            <CalendarIcon className='tw-mr-1' height={20} width={20} />
            <span>
              {i18nT('description.default', {
                date: date && formatDate({ date, format: 'd MMM yyyy' }),
              })}
            </span>
          </div>
          {notes && <div className='tw-mt-2'>{notes}</div>}
          {booker && (
            <div className='tw-mt-2'>
              <p className='tw-mb-0'>
                {i18nTCommon('bookings.bookedBy', {
                  interpolation: { escapeValue: false },
                  name: personDisplayName({
                    attributes: booker.attributes,
                  }),
                })}
                {isBookerDeleted && ` ${i18nTCommon('shared.deletedUser')}`}
              </p>
              {!isBookerDeleted && (
                <a
                  className='app-link tw-align-baseline tw-text-blue-500 hover:tw-text-blue-300 active:tw-text-blue-300'
                  href={`mailto:${bookerEmail}`}
                >
                  {bookerEmail}
                </a>
              )}
            </div>
          )}
        </>
      }
    </div>
  );
}

BookingDetails.propTypes = {
  course: resourceShape('course').isRequired,
  booking: resourceShape('booking').isRequired,
  booker: resourceShape('simpleUser'),
  registration: resourceShape('registration'),
};
